import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

export interface LeafletMapProps {
  center: { lat: number; lng: number };
  zoom?: number;
  height?: number;
  onLocationChange?: (location: { lat: number; lng: number }) => void;
  draggable?: boolean;
  searchRadiusMeters?: number; // Radius in meters to display as a circle
}

const LeafletMap: React.FC<LeafletMapProps> = ({
  center,
  zoom = 15,
  height = 500,
  onLocationChange,
  draggable = true,
  searchRadiusMeters,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current).setView([center.lat, center.lng], zoom);

    // Dark basemap (CARTO dark_all) to match the admin's Grid dark theme.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap contributors © CARTO',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(map);

    // Add marker
    const marker = L.marker([center.lat, center.lng], {
      draggable,
    }).addTo(map);

    markerRef.current = marker;
    mapInstanceRef.current = map;

    // Add search radius circle if provided
    if (searchRadiusMeters && searchRadiusMeters > 0) {
      const circle = L.circle([center.lat, center.lng], {
        radius: searchRadiusMeters,
        color: '#3388ff',
        fillColor: '#3388ff',
        fillOpacity: 0.1,
        weight: 2,
        dashArray: '5, 5',
      }).addTo(map);
      circleRef.current = circle;
    }

    // Handle marker drag
    if (draggable && onLocationChange) {
      marker.on('dragend', (e) => {
        const position = e.target.getLatLng();
        onLocationChange({
          lat: position.lat,
          lng: position.lng,
        });
      });
    }

    // Handle map click (if not draggable, allow clicking to move marker)
    if (!draggable && onLocationChange) {
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        marker.setLatLng([lat, lng]);
        onLocationChange({ lat, lng });
      });
    }

    // Cleanup
    return () => {
      if (circleRef.current) {
        circleRef.current.remove();
      }
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - map initialization

  // Update marker position and circle when center or radius changes externally
  useEffect(() => {
    if (markerRef.current && mapInstanceRef.current) {
      const newLatLng = L.latLng(center.lat, center.lng);
      markerRef.current.setLatLng(newLatLng);
      
      // Update or create circle
      if (searchRadiusMeters && searchRadiusMeters > 0) {
        if (circleRef.current) {
          circleRef.current.setLatLng(newLatLng);
          circleRef.current.setRadius(searchRadiusMeters);
        } else if (mapInstanceRef.current) {
          const circle = L.circle([center.lat, center.lng], {
            radius: searchRadiusMeters,
            color: '#3388ff',
            fillColor: '#3388ff',
            fillOpacity: 0.1,
            weight: 2,
            dashArray: '5, 5',
          }).addTo(mapInstanceRef.current);
          circleRef.current = circle;
        }
      } else if (circleRef.current) {
        // Remove circle if radius is 0 or undefined
        circleRef.current.remove();
        circleRef.current = null;
      }
      
      // Only pan if the change is significant (not from user drag)
      const currentCenter = mapInstanceRef.current.getCenter();
      const distance = currentCenter.distanceTo(newLatLng);
      if (distance > 100) { // Only pan if more than 100m away
        mapInstanceRef.current.panTo(newLatLng);
      }
    }
  }, [center.lat, center.lng, searchRadiusMeters]);

  // Handle map resize when container size changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      // Use setTimeout to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        mapInstanceRef.current?.invalidateSize();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [height]);

  return (
    <div
      ref={mapRef}
      style={{
        height: height > 0 ? `${height}px` : '100%',
        width: '100%',
        borderRadius: '4px',
        zIndex: 0,
      }}
    />
  );
};

export default LeafletMap;

